import { DEFAULT_FLOW_THEME } from "./constants/defaultTheme.js";
import { applyTheme } from "./functions/theme/applyTheme.js";
import { parseThemedMessage } from "./functions/theme/parseThemedMessage.js";
import { selectThemeMode } from "./functions/theme/selectThemeMode.js";
import { detectColorScheme } from "./functions/theme/detectColorScheme.js";
import { serializeUnknownToJson } from "./serializeUnknownToJson.js";
import { getAutoStopAllFromStorage, getAutoStopTopLevelFromStorage, getEndTriggerFromStorage, getEndTriggerModeFromStorage, getStartTriggerFromStorage, getTriggerRearmModeFromStorage, } from "./RuntimeControl.js";
import { matchesTrigger } from "./triggers/matchesTrigger.js";
import { isTriggeredByStart, resetTriggerState, setTriggeredByStart, } from "./triggers/triggerState.js";
/**
 * Default configuration for FlowTracer.
 * @internal Reserved for future config override feature.
 */
const _DEFAULT_CONFIG = {
    // logLevel: "debug",
    // logEnter: true,
    // logExit: true,
    // logTiming: true,
    // logParams: false,
    // logReturnValues: false,
    // warnOnMismatchedExit: true,
    // autoRecoverFromMismatch: true,
    // include: {},
    // exclude: {},
    theme: DEFAULT_FLOW_THEME,
    // startTriggerFunctionName: null,
    // endTriggerFunctionName: null,
    // endTriggerMode: "on-exit",
    // triggerRearmMode: "once",
};
/**
 * Creates a function flow tracer instance.
 * Pure factory function with no side effects.
 *
 * @param logger - Logger instance for output
 * @param config - Configuration options
 * @returns FlowTracer instance
 */
export function createFlowTracer(logger, config) {
    // Merge user config with defaults
    const mergedConfig = {
        ..._DEFAULT_CONFIG,
        ...config,
        theme: config?.theme
            ? { ..._DEFAULT_CONFIG.theme, ...config.theme }
            : _DEFAULT_CONFIG.theme,
    };
    // Detect color scheme for theme selection
    const colorScheme = detectColorScheme();
    // Pre-compute all theme formatters once at creation time to optimize hot path
    const precomputedThemes = {
        functionEnter: {
            options: selectThemeMode(mergedConfig.theme.functionEnter || _DEFAULT_CONFIG.theme.functionEnter, colorScheme),
            icon: mergedConfig.theme.functionEnter?.icon ??
                _DEFAULT_CONFIG.theme.functionEnter.icon,
        },
        functionExit: {
            options: selectThemeMode(mergedConfig.theme.functionExit || _DEFAULT_CONFIG.theme.functionExit, colorScheme),
            icon: mergedConfig.theme.functionExit?.icon ??
                _DEFAULT_CONFIG.theme.functionExit.icon,
        },
        asyncStart: {
            options: selectThemeMode(mergedConfig.theme.asyncStart || _DEFAULT_CONFIG.theme.asyncStart, colorScheme),
            icon: mergedConfig.theme.asyncStart?.icon ??
                _DEFAULT_CONFIG.theme.asyncStart.icon,
        },
        asyncComplete: {
            options: selectThemeMode(mergedConfig.theme.asyncComplete || _DEFAULT_CONFIG.theme.asyncComplete, colorScheme),
            icon: mergedConfig.theme.asyncComplete?.icon ??
                _DEFAULT_CONFIG.theme.asyncComplete.icon,
        },
        parameter: {
            options: selectThemeMode(mergedConfig.theme.parameter || _DEFAULT_CONFIG.theme.parameter, colorScheme),
            icon: mergedConfig.theme.parameter?.icon ??
                _DEFAULT_CONFIG.theme.parameter.icon,
        },
        returnValue: {
            options: selectThemeMode(mergedConfig.theme.returnValue || _DEFAULT_CONFIG.theme.returnValue, colorScheme),
            icon: mergedConfig.theme.returnValue?.icon ??
                _DEFAULT_CONFIG.theme.returnValue.icon,
        },
        exception: {
            options: selectThemeMode(mergedConfig.theme.exception || _DEFAULT_CONFIG.theme.exception, colorScheme),
            icon: mergedConfig.theme.exception?.icon ??
                _DEFAULT_CONFIG.theme.exception.icon,
        },
        runtimeControl: {
            options: selectThemeMode(mergedConfig.theme.runtimeControl ||
                _DEFAULT_CONFIG.theme.runtimeControl, colorScheme),
            icon: mergedConfig.theme.runtimeControl?.icon ??
                _DEFAULT_CONFIG.theme.runtimeControl.icon,
        },
    };
    // Internal state (closures) - currently unused but reserved for future mismatch detection
    // const nextId = 0;
    // const callStack: StackFrame[] = [];
    // Call depth and auto-stop tracking
    let currentDepth = 0;
    let topLevelCallCount = 0;
    let totalCallCount = 0;
    let autoStopCallback = null;
    let autoStartCallback = null;
    let isEnabledCallback = null;
    // Cached auto-stop limits (read once, updated via setters)
    let cachedTopLevelLimit = null;
    let cachedAllLimit = null;
    // Cached trigger settings (read once, updated via setters)
    let cachedStartTrigger = null;
    let cachedEndTrigger = null;
    let cachedEndTriggerMode = "on-exit";
    let cachedTriggerRearmMode = "once";
    /**
     * Gets the current canonical outputMode.
     *
     * Side effects: reads global AutoTracer internal state.
     *
     * @returns Current outputMode when available; otherwise defaults to "devtools".
     */
    function getOutputModeOrDevtools() {
        const mode = globalThis.__autoTracerInternal?.outputMode;
        return mode === "copy-paste" ? "copy-paste" : "devtools";
    }
    /**
     * Formats a trace value based on the canonical outputMode.
     * Sanitizes React elements to prevent logging internal fiber objects.
     *
     * Side effects: reads global AutoTracer internal state.
     *
     * @param value - The value to format.
     * @returns The original value in devtools mode (sanitized if React element); a JSON string for object values in copy-paste mode.
     */
    function formatValueForOutputMode(value) {
        if (typeof value !== "object" || value === null)
            return value;
        // Sanitize React elements by removing internal fiber properties
        if (isReactElement(value)) {
            return sanitizeReactElement(value);
        }
        const mode = getOutputModeOrDevtools();
        if (mode !== "copy-paste")
            return value;
        return serializeUnknownToJson(value);
    }
    /**
     * Check if a value is a React element by detecting React-specific properties.
     */
    function isReactElement(value) {
        if (typeof value !== "object" || value === null)
            return false;
        const obj = value;
        // Check for React element markers: $$typeof, _owner, type, props
        return (("$$typeof" in obj || "_owner" in obj || "type" in obj) && "props" in obj);
    }
    /**
     * Remove React fiber internals from a React element to make it loggable.
     */
    function sanitizeReactElement(element) {
        if (typeof element !== "object" || element === null)
            return element;
        const obj = element;
        const sanitized = {};
        // Keep only the essential React element properties
        if ("type" in obj) {
            const type = obj.type;
            // For function/class components, log the name or function
            if (typeof type === "function") {
                sanitized.type = type.name || `${type.toString().substring(0, 50)}...`;
            }
            else {
                sanitized.type = type;
            }
        }
        if ("props" in obj && typeof obj.props === "object" && obj.props !== null) {
            // Shallow copy props, but don't go into nested React elements
            const props = obj.props;
            const sanitizedProps = {};
            for (const [key, value] of Object.entries(props)) {
                if (key === "children" && isReactElement(value)) {
                    sanitizedProps.children = "[React Element]";
                }
                else if (Array.isArray(value)) {
                    sanitizedProps[key] = value.map((v) => {
                        return isReactElement(v) ? "[React Element]" : v;
                    });
                }
                else if (isReactElement(value)) {
                    sanitizedProps[key] = "[React Element]";
                }
                else {
                    sanitizedProps[key] = value;
                }
            }
            sanitized.props = sanitizedProps;
        }
        if ("key" in obj)
            sanitized.key = obj.key;
        return sanitized;
    }
    /**
     * Marks synchronous function entry.
     */
    function enter(functionName, ...optionalParams) {
        // Check for start trigger (if tracer is stopped and trigger is configured)
        const startTriggerPattern = cachedStartTrigger;
        const isTracerEnabled = isEnabledCallback?.() ?? false; // Default to false if callback not set
        const alreadyFiredOnce = cachedTriggerRearmMode === "once" && isTriggeredByStart();
        if (!isTracerEnabled &&
            startTriggerPattern &&
            !alreadyFiredOnce &&
            matchesTrigger(functionName, startTriggerPattern)) {
            setTriggeredByStart(true);
            if (autoStartCallback) {
                autoStartCallback();
            }
        }
        // Check for end trigger (on-entry mode)
        const endTriggerPattern = cachedEndTrigger;
        const endTriggerMode = cachedEndTriggerMode;
        const triggerRearmMode = cachedTriggerRearmMode;
        // Re-query enabled state (may have changed if start trigger just fired)
        const isTracerEnabledNow = isEnabledCallback?.() ?? false;
        if (endTriggerMode === "on-entry" &&
            isTracerEnabledNow &&
            endTriggerPattern &&
            matchesTrigger(functionName, endTriggerPattern)) {
            // Stop immediately to prevent excessive logging
            if (triggerRearmMode === "always") {
                resetTriggerState();
            }
            autoStopCallback?.();
        }
        // Track depth (incremented on entry, decremented on exit)
        currentDepth++;
        const { options, icon } = precomputedThemes.functionEnter;
        const themedMessage = applyTheme(functionName, options, icon);
        const { message, css } = parseThemedMessage(themedMessage);
        return css
            ? logger.enterStyled(functionName, message, css, ...optionalParams)
            : logger.enterStyled(functionName, message, ...optionalParams);
    }
    /**
     * Marks synchronous function exit.
     */
    function exit(handle) {
        // Decrement depth
        currentDepth = Math.max(0, currentDepth - 1);
        // Track counts (incremented on exit to count completed functions)
        const exitDepth = currentDepth; // Depth AFTER decrement
        totalCallCount++;
        if (exitDepth === 0) {
            topLevelCallCount++;
        }
        // Check auto-stop limits after function completes
        if ((cachedTopLevelLimit !== null &&
            topLevelCallCount >= cachedTopLevelLimit &&
            exitDepth === 0) ||
            (cachedAllLimit !== null && totalCallCount >= cachedAllLimit)) {
            if (autoStopCallback) {
                // Stop immediately to prevent excessive logging
                const reason = cachedTopLevelLimit !== null &&
                    topLevelCallCount >= cachedTopLevelLimit
                    ? `top-level limit (${cachedTopLevelLimit})`
                    : `total limit (${cachedAllLimit})`;
                logger.log(`Flow tracer auto-stop triggered: reached ${reason}`);
                autoStopCallback();
            }
        }
        const { options, icon } = precomputedThemes.functionExit;
        const themedMessage = applyTheme(handle.rawLabel, options, icon);
        const { message, css } = parseThemedMessage(themedMessage);
        if (css) {
            logger.exitStyled(handle, message, css);
        }
        else {
            logger.exitStyled(handle, message);
        }
        // Check for end trigger (on-exit mode)
        const endTriggerPattern = cachedEndTrigger;
        const endTriggerMode = cachedEndTriggerMode;
        const triggerRearmMode = cachedTriggerRearmMode;
        const isTracerEnabled = isEnabledCallback?.() ?? false; // Default to false if callback not set
        if (endTriggerMode === "on-exit" &&
            isTracerEnabled &&
            endTriggerPattern &&
            matchesTrigger(handle.rawLabel, endTriggerPattern)) {
            // Stop after logging to include the last statement
            if (triggerRearmMode === "always") {
                resetTriggerState();
            }
            autoStopCallback?.();
        }
    }
    /**
     * Marks async function entry.
     * Uses flat trace message (NOT console.group) because async operations don't nest cleanly.
     * Spec: docs/work/spec/flow-theme-system-design.md lines 47-48
     */
    function enterAsync(functionName, ...optionalParams) {
        const startTime = performance.now();
        const { options, icon } = precomputedThemes.asyncStart;
        const themedMessage = applyTheme(`${functionName} (async started)`, options, icon);
        const { message, css } = parseThemedMessage(themedMessage);
        // Use trace() for flat output, NOT enterStyled() which creates console.group
        if (css) {
            logger.trace(message, css, ...optionalParams);
        }
        else {
            logger.trace(message, ...optionalParams);
        }
        // Return handle for timing without creating a group
        const handle = {
            rawLabel: functionName,
            label: message,
            startTime,
            level: "trace",
        };
        return handle;
    }
    /**
     * Marks async function exit.
     * Uses flat trace message (NOT console.groupEnd) because async operations don't nest cleanly.
     * Spec: docs/work/spec/flow-theme-system-design.md lines 47-48
     */
    function exitAsync(handle) {
        const elapsed = performance.now() - handle.startTime;
        const { options, icon } = precomputedThemes.asyncComplete;
        const themedMessage = applyTheme(`${handle.rawLabel} (async completed, elapsed: ${elapsed.toFixed(1)}ms)`, options, icon);
        const { message, css } = parseThemedMessage(themedMessage);
        // Use trace() for flat output, NOT exitStyled() which creates console.groupEnd
        if (css) {
            logger.trace(message, css);
        }
        else {
            logger.trace(message);
        }
    }
    /**
     * Logs a parameter with themed styling.
     */
    function traceParameter(name, value) {
        const { options, icon } = precomputedThemes.parameter;
        const themedMessage = applyTheme(`param ${name}:`, options, icon);
        const { message, css } = parseThemedMessage(themedMessage);
        const formattedValue = formatValueForOutputMode(value);
        if (css) {
            logger.trace(message, css, formattedValue);
        }
        else {
            logger.trace(message, formattedValue);
        }
    }
    /**
     * Logs a return value with themed styling.
     */
    function traceReturnValue(value) {
        const { options, icon } = precomputedThemes.returnValue;
        const themedMessage = applyTheme("returned:", options, icon);
        const { message, css } = parseThemedMessage(themedMessage);
        const formattedValue = formatValueForOutputMode(value);
        if (css) {
            logger.trace(message, css, formattedValue);
        }
        else {
            logger.trace(message, formattedValue);
        }
    }
    /**
     * Logs an exception with themed styling.
     */
    function traceException(functionName, error) {
        const { options, icon } = precomputedThemes.exception;
        const themedMessage = applyTheme(`Exception in ${functionName}:`, options, icon);
        const { message, css } = parseThemedMessage(themedMessage);
        const formattedError = formatValueForOutputMode(error);
        if (css) {
            logger.debug(message, css, formattedError);
        }
        else {
            logger.debug(message, formattedError);
        }
    }
    /**
     * Logs a trace message (passthrough to logger).
     */
    function trace(message, ...optionalParams) {
        logger.trace(message, ...optionalParams);
    }
    /**
     * Logs a debug message (passthrough to logger).
     */
    function debug(message, ...optionalParams) {
        logger.debug(message, ...optionalParams);
    }
    /**
     * Sets the auto-stop callback.
     * @internal Used by runtime controls
     */
    function setAutoStopCallback(callback) {
        autoStopCallback = callback;
    }
    /**
     * Sets the auto-start callback (for triggers).
     * @internal Used by runtime controls
     */
    function setAutoStartCallback(callback) {
        autoStartCallback = callback;
    }
    /**
     * Sets the is-enabled callback (for trigger logic to query tracer state).
     * @internal Used by runtime controls
     */
    function setIsEnabledCallback(callback) {
        isEnabledCallback = callback;
    }
    /**
     * Gets current top-level call count.
     * @internal Used by runtime controls
     */
    function getTopLevelCount() {
        return topLevelCallCount;
    }
    /**
     * Gets current total call count.
     * @internal Used by runtime controls
     */
    function getTotalCount() {
        return totalCallCount;
    }
    /**
     * Resets all counters and depth.
     * @internal Used by runtime controls
     */
    function resetCounts() {
        currentDepth = 0;
        topLevelCallCount = 0;
        totalCallCount = 0;
    }
    /**
     * Updates cached auto-stop limits.
     * @internal Used by runtime controls when limits are changed
     */
    function updateCachedLimits(topLevel, all) {
        cachedTopLevelLimit = topLevel;
        cachedAllLimit = all;
    }
    /**
     * Syncs cached limits from localStorage.
     * @internal Called when tracer starts
     */
    function syncLimitsFromStorage() {
        cachedTopLevelLimit = getAutoStopTopLevelFromStorage();
        cachedAllLimit = getAutoStopAllFromStorage();
    }
    /**
     * Updates cached start trigger pattern.
     * @internal Used by runtime controls when trigger is changed
     */
    function updateCachedStartTrigger(pattern) {
        cachedStartTrigger = pattern;
    }
    /**
     * Updates cached end trigger pattern.
     * @internal Used by runtime controls when trigger is changed
     */
    function updateCachedEndTrigger(pattern) {
        cachedEndTrigger = pattern;
    }
    /**
     * Updates cached end trigger mode.
     * @internal Used by runtime controls when mode is changed
     */
    function updateCachedEndTriggerMode(mode) {
        cachedEndTriggerMode = mode;
    }
    /**
     * Updates cached trigger re-arm mode.
     * @internal Used by runtime controls when mode is changed
     */
    function updateCachedTriggerRearmMode(mode) {
        cachedTriggerRearmMode = mode;
    }
    /**
     * Syncs cached trigger settings from localStorage.
     * @internal Called when tracer starts
     */
    function syncTriggerSettingsFromStorage() {
        cachedStartTrigger = getStartTriggerFromStorage();
        cachedEndTrigger = getEndTriggerFromStorage();
        cachedEndTriggerMode = getEndTriggerModeFromStorage();
        cachedTriggerRearmMode = getTriggerRearmModeFromStorage();
    }
    return {
        enter,
        exit,
        enterAsync,
        exitAsync,
        traceParameter,
        traceReturnValue,
        traceException,
        trace,
        debug,
        // Internal methods for runtime controls (not part of public interface)
        setAutoStopCallback,
        setAutoStartCallback,
        setIsEnabledCallback,
        getTopLevelCount,
        getTotalCount,
        resetCounts,
        updateCachedLimits,
        syncLimitsFromStorage,
        updateCachedStartTrigger,
        updateCachedEndTrigger,
        updateCachedEndTriggerMode,
        updateCachedTriggerRearmMode,
        syncTriggerSettingsFromStorage,
    };
}
// Intentionally no auto-initialization.
